<template>
    <page-layout :hide-footer="false" :body-style="{ paddingTop: 0 }" :hero-image-url="brief && brief.activation.image">
        <g-loader v-if="isLoading || !brief" type="fixed" />
        <template v-else>
            <header class="header g-mt-5">
                <g-button
                    icon="angle-left"
                    class="margin-bottom-normal pdf-ignore"
                    size="small"
                    :disabled="isPreview"
                    @click="$root.go('sponsorships')"
                >
                    Back
                </g-button>
                <g-tag v-if="pastBriefEndDate" class="ended-tag" :theme="gray">Ended</g-tag>
            </header>
            <title-bar :title="brandName" />
            <template slot="default">
                <div class="step-card-wrapper">
                    <g-typography tag="h1" font-weight="semibold">
                        {{ publicName }}
                    </g-typography>
                </div>
                <template v-if="pastBriefEndDate">
                    <base-box class="g-mb-3 past-brief-end-date-box">
                        <div slot="content">
                            <g-typography tag="p">
                                {{ brandName }} is no longer accepting submissions for proposals. Please contact
                                <a :href="`mailto:${(brief && brief.brand && brief.brand.support_email) || ''}`">{{ (brief && brief.brand && brief.brand.support_email) || '' }}</a> if
                                you have any questions.
                            </g-typography>
                        </div>
                    </base-box>
                </template>
                <div v-if="brief && brief.activation && brief.activation.description" class="overview-section">
                    <g-typography
                        class="overview-section-title"
                        tag="h4"
                        :value="getCustomizableTitle('overview_title')"
                        font-weight="semibold"
                    />
                    <div v-html="$sanitize(brief.activation.description)"></div>
                </div>

                <div>
                    <g-row
                        v-if="briefProposalStart || briefProposalEnd"
                        type="flex"
                        justify="space-between"
                        align="top"
                        class="overview-section"
                        :gutter="[16, 0]"
                    >
                        <g-column v-if="briefProposalStart" :span="12">
                            <div>
                                <g-typography
                                    class="overview-section-title"
                                    tag="h4"
                                    :value="getCustomizableTitle('campaign_date_start_title')"
                                    font-weight="semibold"
                                />
                                <g-typography tag="div" :value="formatDate(briefProposalStart)" />
                            </div>
                        </g-column>
                        <g-column v-if="briefProposalEnd" :span="12">
                            <div>
                                <g-typography
                                    class="overview-section-title"
                                    tag="h4"
                                    :value="getCustomizableTitle('campaign_date_end_title')"
                                    font-weight="semibold"
                                />
                                <g-typography tag="div" :value="formatDate(briefProposalEnd)" />
                            </div>
                        </g-column>
                    </g-row>

                    <g-row
                        v-if="contentDeliverStart || contentDeliverEnd"
                        type="flex"
                        justify="space-between"
                        align="top"
                        class="overview-section"
                        :gutter="[16, 0]"
                    >
                        <g-column v-if="contentDeliverStart" :span="12">
                            <div>
                                <g-typography
                                    class="overview-section-title"
                                    tag="h4"
                                    :value="getCustomizableTitle('content_date_start_title')"
                                    font-weight="semibold"
                                />
                                <g-typography tag="div" :value="formatDate(contentDeliverStart)" />
                            </div>
                        </g-column>
                        <g-column v-if="contentDeliverEnd" :span="12">
                            <div>
                                <g-typography
                                    class="overview-section-title"
                                    tag="h4"
                                    :value="getCustomizableTitle('content_date_end_title')"
                                    font-weight="semibold"
                                />
                                <g-typography tag="div" :value="formatDate(contentDeliverEnd)" />
                            </div>
                        </g-column>
                    </g-row>
                </div>

                <!-- Products -->
                <div v-if="hasProductConfig" class="overview-section">
                    <g-typography
                        class="overview-section-title"
                        tag="h4"
                        :value="getCustomizableTitle('products_title')"
                        font-weight="semibold"
                    />
                    <p>{{ getConfigText }}</p>
                </div>

                <!-- Tasks -->
                <div v-if="brief && Array.isArray(brief.tasks) && brief.tasks.length > 0" class="overview-section">
                    <g-typography
                        class="overview-section-title"
                        tag="h4"
                        :value="getCustomizableTitle('live_tasks_title')"
                        font-weight="semibold"
                    />
                    <div v-for="task in (brief.tasks || [])" :key="task.id">
                        <div class="overview-section-task">
                            <activation-task-icon :task="task" />
                            <span>{{ task.name }}</span>
                            <span v-if="task.quantity > 0">({{ task.quantity }})</span>
                        </div>
                    </div>
                </div>

                <!-- Payments -->
                <div v-if="compensationText" class="overview-section">
                    <g-typography
                        class="overview-section-title"
                        tag="h4"
                        :value="getCustomizableTitle('live_payment_title')"
                        font-weight="semibold"
                    />
                    <p>{{ compensationText }}</p>
                </div>

                <!-- Brand Assets -->
                <div v-if="brandAssets.length > 0" class="overview-section">
                    <g-typography
                        class="overview-section-title"
                        tag="h4"
                        :value="getCustomizableTitle('brand_assets_title')"
                        font-weight="semibold"
                    />
                    <div v-for="asset in brandAssets" :key="asset.id">
                        <activations-brand-asset :asset="asset" />
                    </div>
                </div>

                <!-- About the Brand -->
                <div v-if="brief && brief.brand && brief.brand.description" class="overview-section">
                    <g-typography
                        class="overview-section-title"
                        tag="h4"
                        value="About the Brand"
                        font-weight="semibold"
                    />
                    <p>{{ (brief.brand && brief.brand.description) || '' }}</p>
                </div>
            </template>
            <template v-if="!isBriefAccepted && isBriefSubmittable" slot="footer">
                <div class="footer-container">
                    <g-button
                        :loading="false"
                        shape="round"
                        theme="live"
                        type="primary"
                        size="xlarge"
                        :disabled="isPreview"
                        @click="$router.push({ name: 'v2.activation.brief.overview' })"
                    >
                        {{ getStartedButtonText }}
                    </g-button>
                </div>
            </template>
            <template v-if="!isBriefSubmissionActive && !pastBriefEndDate" slot="footer">
                <div style="text-align: center">
                    <div style="margin: 16px; padding: 16px">
                        <template v-if="beforeBriefStartDate">
                            <activation-brief-submission-countdown
                                :end-datetime="brief.activation.brief_submission_start_at"
                                @countdown-complete="() => briefRecomputeSubmissionDates()"
                                font-weight="bold"
                                italic
                            />
                        </template>
                    </div>
                </div>
                <div class="footer-container">
                    <g-button
                        style="opacity: 0.5"
                        :loading="false"
                        shape="round"
                        theme="live"
                        type="primary"
                        size="xlarge"
                        :disabled="true"
                        @click="$router.push({ name: 'v2.activation.brief.overview' })"
                    >
                        {{ getStartedButtonText }}
                    </g-button>
                </div>
            </template>
        </template>
    </page-layout>
</template>

<script>
    import { get } from 'lodash';
    import TitleBar from '../../library/TitleBar.vue';
    import PageLayout from '../../layouts/PageLayout.vue';
    import ActivationBriefMixin from './mixins/ActivationBriefMixin';
    import { getActivationFiles } from '../../../helpers/activations/activation-files';
    import { getFileDisplayNames } from '../../../helpers/file-display-names';
    import { getCustomizableTitle } from '../../../helpers/activations/default-texts';
    import { formatCurrency } from '../../../helpers/formatters';
    import { BRAND_ASSET_FILE_TYPE } from '../../../constants/activation-files';
    import {
        PAYMENT_TYPE_UPFRONT,
        PAYMENT_TYPE_UPON_COMPLETION,
        PAYMENT_TYPE_SENTENCE_PREDICATE
    } from '../../../constants/activation-payments';
    import ActivationsBrandAsset from '../../library/ActivationsBrandAsset.vue';
    import { clampString } from '../../../helpers/string-helpers';
    import moment from 'moment-timezone';
    import ActivationBriefProductConfigMixin from './mixins/ActivationBriefProductConfigMixin';
    import ActivationTaskIcon from './partials/tasks/ActivationTaskIcon.vue';
    import ActivationBriefSubmissionCountdown from './partials/steps/ActivationBriefSubmissionCountdown.vue';

    export default {
        name: 'ActivationBriefView',
        components: {
            PageLayout,
            TitleBar,
            ActivationsBrandAsset,
            ActivationTaskIcon,
            ActivationBriefSubmissionCountdown
        },
        mixins: [ActivationBriefMixin, ActivationBriefProductConfigMixin],
        props: {
            brief: {
                type: Object,
                default: null
            },
            contact: {
                type: Object,
                default: null
            },
            isLoading: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            brandAssets() {
                return getActivationFiles(this.brief, BRAND_ASSET_FILE_TYPE);
            },
            publicName() {
                return clampString(get(this, 'brief.activation.public_name'), 75);
            },
            brandName() {
                return get(this, 'brief.brand.name', '');
            },
            getStartedButtonText() {
                return clampString(this.getCustomizableTitle('get_started_button_text'), 60);
            },
            isWhiteLabeled() {
                return get(this, 'brief.is_white_labeled', false);
            },
            compensationText() {
                let paragraph = '';
                this.brief.payment_configs.forEach((config, idx) => {
                    if ([PAYMENT_TYPE_UPFRONT, PAYMENT_TYPE_UPON_COMPLETION].includes(config.type)) {
                        const sentenceSubject = idx > 0 ? "You'll also" : "You'll";
                        const amount = formatCurrency(config.amount, get(this, 'brief.brand.currency', 'usd'));
                        const sentence = `${sentenceSubject} be paid ${amount} ${
                            PAYMENT_TYPE_SENTENCE_PREDICATE[config.type]
                        }`;
                        paragraph = `${paragraph} ${sentence}`;
                    }
                });
                return paragraph;
            },
            briefProposalStart() {
                return get(this, 'brief.activation.brief_submission_start_at');
            },
            briefProposalEnd() {
                return get(this, 'brief.activation.brief_submission_end_at');
            },
            contentDeliverStart() {
                return get(this, 'brief.content_tracking_start_at');
            },
            contentDeliverEnd() {
                return get(this, 'brief.content_tracking_end_at');
            }
        },
        methods: {
            assetType(mimeType) {
                return getFileDisplayNames(mimeType);
            },
            getCustomizableTitle(key) {
                return getCustomizableTitle(this.brief, key);
            },
            download(asset) {
                window.open(asset.url, '_blank');
            },
            formatDate(isoDate) {
                return moment(isoDate).format('MMM D, YYYY');
            }
        }
    };
</script>

<style lang="scss" scoped>
    .header {
        display: flex;
        justify-content: space-between;
        width: 100%;
    }
    .overview-section {
        margin-bottom: $g-spacing-5;
        .overview-section-title {
            margin-bottom: $g-spacing-2;
        }
    }

    .overview-section-task {
        display: flex;
        align-items: center;

        > span:first-child {
            margin-right: $g-spacing-2;
        }
    }

    .step-card-wrapper {
        margin-bottom: $g-spacing-5;
    }

    .no-margins {
        & ::v-deep p {
            margin: 0;
        }
    }

    .margin-bottom-tiny {
        margin-bottom: $g-spacing-1;
    }

    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .profile-info {
        padding-left: $g-spacing-3;
    }

    .powered-by-grin {
        text-align: center;
        margin-top: $g-spacing-4;
    }
    .footer-container {
        display: flex;
        justify-content: center;
    }
    .ended-tag {
        height: 25px;
    }
    .past-brief-end-date-box {
        background-color: $g-color-primary-1;
    }
</style>
